<template>
  <div class="tts-wrapper">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          TTS
        </h1>
      </div>

      <div class="main-content main-content--split-row">
        <div class="left-panel">
          <div class="form-content">
            <div class="form-group">
              <label class="form-label required">
                Model
                <span class="required-asterisk">*</span>
              </label>
              <MankSelect
                v-model="formData.model"
                :items="modelOptions"
                :loading="modelsLoading"
                item-text="label"
                item-value="value"
                placeholder="Select model"
                class="tts-select"
                density="compact"
                @change="onModelChange">
                <template v-slot:append>
                  <Icon name="expand-down" variant="solid" color="muted" size="20"/>
                </template>
              </MankSelect>
            </div>
            <div class="form-group">
              <label class="form-label required">
                Voice
                <span class="required-asterisk">*</span>
              </label>
              <MankSelect
                v-model="formData.speakerId"
                :items="voiceOptions"
                item-text="name"
                item-value="id"
                placeholder="Select voice"
                class="tts-select"
                :loading="voicesLoading"
                density="compact">
                <template v-slot:selection="{ item }">
                  {{ item.name }}
                </template>
                <template v-slot:item="{ item }">
                  {{ item.name }}
                </template>
                <template v-slot:append>
                  <Icon name="expand-down" variant="solid" color="muted" size="20"/>
                </template>
              </MankSelect>
            </div>
            <div
              v-for="p in schemaExtraParams"
              :key="paramName(p)"
              class="form-group">
              <label :class="['form-label', (p.required ?? p.Required) ? 'required' : '']">
                {{ humanParamLabel(p) }}
                <span
                  v-if="p.required ?? p.Required"
                  class="required-asterisk">*</span>
              </label>
              <MankTextField
                v-if="paramTypeIs(p, 'string')"
                v-model="extraParamValues[paramName(p)]"
                dense
                hide-details="auto"
                class="form-input"
                :placeholder="paramName(p)"/>
              <MankTextarea
                v-else-if="paramTypeIs(p, 'text')"
                v-model="extraParamValues[paramName(p)]"
                variant="outlined"
                hide-details="auto"
                rows="4"
                class="form-input"/>
              <MankTextField
                v-else-if="paramTypeIs(p, 'int') || paramTypeIs(p, 'float')"
                v-model.number="extraParamValues[paramName(p)]"
                type="number"
                step="any"
                dense
                hide-details="auto"
                class="form-input"
                :placeholder="paramName(p)"/>
              <v-checkbox
                v-else-if="paramTypeIs(p, 'bool')"
                v-model="extraParamValues[paramName(p)]"
                dense
                hide-details
                class="mt-0 pt-0 param-bool-checkbox"/>
              <div
                v-else-if="paramTypeIs(p, 'file')"
                class="file-param-field">
                <v-file-input
                  :key="`tts-file-${paramName(p)}-${(fileInputKeys[paramName(p)] || 0)}`"
                  v-model="extraParamValues[paramName(p)]"
                  placeholder="Choose file"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  :truncate-length="36"
                  class="form-input mank-input tts-file-input"
                  @change="onParamFileUpdated(p)">
                  <template v-slot:append>
                    <Icon name="upload" variant="solid" color="muted" size="20"/>
                  </template>
                </v-file-input>
                <p
                  v-if="paramFileMaxMb(p)"
                  class="helper-text mt-1 mb-0">
                  Max {{ paramFileMaxMb(p) }} MB
                </p>
              </div>
            </div>
            <div class="form-actions">
              <v-btn
                color="primary"
                class="mank-btn"
                elevation="0"
                :loading="generating"
                :disabled="!canGenerate || !extraParamsSatisfied"
                @click="generate">
                <v-icon left size="20">
                  mdi-play
                </v-icon>
                Generate
              </v-btn>
            </div>
          </div>
        </div>

        <div class="right-panel">
          <div class="form-group output-panel-group">
            <h3 class="form-label output-panel-title ds-panel-title">
              Output
            </h3>
            <div class="playback-section">
              <div v-if="!audioUrl" class="empty-playback">
                <v-icon size="48" color="grey-lighten-1">
                  mdi-volume-off
                </v-icon>
                <p class="text-body-2 text--secondary mt-2">
                  Generate speech to play audio here.
                </p>
              </div>
              <div v-else class="audio-player-wrap">
                <audio
                  ref="audioEl"
                  controls
                  class="audio-player"
                  :src="audioUrl"
                  @ended="onAudioEnded"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '../components/Icon.vue';
import AgentService from '../services/AgentService';
import TtsService from '../services/TtsService';
import AnalyticsService from '../services/AnalyticsService';
import { EVENTS } from '../constants/analyticsEvents';

export default {
  name: 'TTS',
  components: { Icon },
  data() {
    return {
      formData: {
        model: '',
        speakerId: null,
      },
      voices: [],
      voicesLoading: false,
      modelsLoading: false,
      generating: false,
      audioUrl: null,
      modelOptions: [],
      modelSchema: null,
      extraParamValues: {},
      /** Remount v-file-input after rejected oversized file */
      fileInputKeys: {},
    };
  },
  computed: {
    voiceOptions() {
      return (this.voices || []).map((s) => ({ id: s.id, name: s.name || `Speaker ${s.id}` }));
    },
    canGenerate() {
      return !!this.formData.model && this.formData.speakerId != null;
    },
    schemaExtraParams() {
      if (!this.modelSchema) return [];
      const raw = this.modelSchema.params ?? this.modelSchema.Params ?? [];
      if (!Array.isArray(raw)) return [];
      return raw.filter((p) => {
        const name = String(p.name ?? p.Name ?? '').trim();
        return name && name.toLowerCase() !== 'speaker_id';
      });
    },
    extraParamsSatisfied() {
      return this.schemaExtraParams.every((p) => {
        if (!(p.required ?? p.Required)) return true;
        const name = this.paramName(p);
        const v = this.extraParamValues[name];
        if (this.paramTypeIs(p, 'file')) {
          return !!this.coerceToUploadFile(v);
        }
        if (this.paramTypeIs(p, 'bool')) return true;
        if (this.paramTypeIs(p, 'int') || this.paramTypeIs(p, 'float')) {
          return !(v === '' || v == null || Number.isNaN(Number(v)));
        }
        return !!String(v == null ? '' : v).trim();
      });
    },
  },
  async mounted() {
    await this.loadModels();
    await this.loadVoices();
  },
  beforeDestroy() {
    this.revokeAudioUrl();
  },
  methods: {
    normalizeAgentParamType(p) {
      const t = String(p.type ?? p.Type ?? 'string').trim().toLowerCase();
      if (t === 'boolean') return 'bool';
      if (t === 'form_file' || t === 'formfile' || t === 'file') return 'file';
      return t;
    },
    paramName(p) {
      return String(p.name ?? p.Name ?? '').trim();
    },
    /** Snake_case / param adlarını okunur, her kelime büyük harfle başlayan etikete çevirir. */
    humanParamLabel(p) {
      const n = this.paramName(p);
      if (!n) return '';
      const spaced = n.replace(/_/g, ' ').trim();
      return spaced
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    },
    paramTypeIs(p, kind) {
      return this.normalizeAgentParamType(p) === kind;
    },
    /** Same idea as TtsService (File/Blob/array/cross-realm); must match synthesize multipart detection. */
    coerceToUploadFile(val) {
      if (val == null) return null;
      if (typeof File !== 'undefined' && val instanceof File) return val;
      if (Object.prototype.toString.call(val) === '[object File]') return val;
      if (Array.isArray(val) && val.length) {
        const x = val[0];
        if (x != null && typeof File !== 'undefined' && x instanceof File) return x;
        if (Object.prototype.toString.call(x) === '[object File]') return x;
      }
      if (typeof Blob !== 'undefined' && val instanceof Blob) return val;
      return null;
    },
    paramFileMaxMb(p) {
      if (!p || !this.paramTypeIs(p, 'file')) return null;
      const m = p.max ?? p.Max;
      if (m === '' || m == null) return null;
      const n = Number(m);
      if (!Number.isFinite(n) || n <= 0) return null;
      return n;
    },
    bumpFileInputKey(name) {
      this.$set(this.fileInputKeys, name, (this.fileInputKeys[name] || 0) + 1);
    },
    onParamFileUpdated(p) {
      const name = this.paramName(p);
      this.$nextTick(() => {
        const raw = this.extraParamValues[name];
        let f = this.coerceToUploadFile(raw);
        if (raw != null && !f) {
          this.$set(this.extraParamValues, name, null);
          return;
        }
        if (f && f !== raw) {
          this.$set(this.extraParamValues, name, f);
          f = this.coerceToUploadFile(this.extraParamValues[name]);
        }
        if (!f) return;
        const maxMb = this.paramFileMaxMb(p);
        if (maxMb != null && f.size > maxMb * 1024 * 1024) {
          this.bumpFileInputKey(name);
          this.$set(this.extraParamValues, name, null);
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: `File must be at most ${maxMb} MB.`,
          });
        }
      });
    },
    initExtraParamValues() {
      const next = {};
      this.schemaExtraParams.forEach((p) => {
        const name = this.paramName(p);
        if (!name) return;
        const t = this.normalizeAgentParamType(p);
        if (t === 'bool') {
          const def = p.default_value ?? p.DefaultValue;
          const truthy = def === true || def === 'true' || def === '1' || def === 1;
          next[name] = truthy;
        } else if (t === 'file') {
          next[name] = null;
        } else if (t === 'text') {
          next[name] = '';
        } else if (t === 'int' || t === 'float') {
          const d = p.default_value ?? p.DefaultValue;
          next[name] = d !== undefined && d !== '' && d != null ? d : '';
        } else {
          next[name] = String(p.default_value ?? p.DefaultValue ?? '');
        }
      });
      this.fileInputKeys = {};
      this.extraParamValues = next;
    },
    async loadModelSchema() {
      this.modelSchema = null;
      this.extraParamValues = {};
      this.fileInputKeys = {};
      const id = (this.formData.model || '').trim();
      if (!id) return;
      try {
        const res = await AgentService.getAgentModelById(id);
        if (res?.success && res.data) {
          this.modelSchema = res.data;
          this.$nextTick(() => this.initExtraParamValues());
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: e.response?.data?.message || e.message || 'Failed to load model parameters.',
        });
      }
    },
    buildExtraParamsForRequest() {
      return this.schemaExtraParams.reduce((out, p) => {
        const name = this.paramName(p);
        const t = this.normalizeAgentParamType(p);
        const v = this.extraParamValues[name];
        if (t === 'file') {
          const f = this.coerceToUploadFile(v);
          if (f) out[name] = f;
          return out;
        }
        if (v === '' || v == null) return out;
        if (t === 'bool') {
          out[name] = !!v;
        } else if (t === 'int') {
          out[name] = parseInt(v, 10);
        } else if (t === 'float') {
          out[name] = Number(v);
        } else {
          out[name] = String(v);
        }
        return out;
      }, {});
    },
    async onModelChange() {
      this.formData.speakerId = null;
      await Promise.all([this.loadVoices(), this.loadModelSchema()]);
    },
    async loadModels() {
      this.modelsLoading = true;
      this.modelOptions = [];
      try {
        const res = await AgentService.getAgentModels({ source: 'TTS' });
        if (res?.success && Array.isArray(res.data)) {
          this.modelOptions = res.data.map((m) => ({
            label: m.name ?? m.Name ?? m.id,
            value: m.id,
          }));
          if (this.modelOptions.length) {
            const current = this.formData.model;
            const stillValid = current && this.modelOptions.some((o) => o.value === current);
            this.formData.model = stillValid ? current : this.modelOptions[0].value;
            await this.loadModelSchema();
          } else {
            this.formData.model = '';
            this.modelSchema = null;
            this.extraParamValues = {};
            this.fileInputKeys = {};
          }
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: e.response?.data?.message || e.message || 'Failed to load TTS models.',
        });
      } finally {
        this.modelsLoading = false;
      }
    },
    async loadVoices() {
      this.voicesLoading = true;
      this.voices = [];
      try {
        const res = await TtsService.getVoices();
        if (res && res.success && res.data) {
          const speakers = res.data.speakers || [];
          this.voices = speakers;
          if (this.voices.length && this.formData.speakerId == null) {
            this.formData.speakerId = this.voices[0].id;
          }
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: e.response?.data?.message || e.message || 'Failed to load voices.',
        });
      } finally {
        this.voicesLoading = false;
      }
    },
    revokeAudioUrl() {
      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl);
        this.audioUrl = null;
      }
    },
    onAudioEnded() {
      // optional: clear after play
    },
    async generate() {
      const badFile = this.schemaExtraParams.find((p) => {
        if (!this.paramTypeIs(p, 'file')) return false;
        const v = this.extraParamValues[this.paramName(p)];
        const maxMb = this.paramFileMaxMb(p);
        const f = this.coerceToUploadFile(v);
        return f && maxMb != null && f.size > maxMb * 1024 * 1024;
      });
      if (badFile) {
        const cap = this.paramFileMaxMb(badFile);
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: `File for "${this.paramName(badFile)}" must be at most ${cap} MB.`,
        });
        return;
      }
      this.generating = true;
      this.revokeAudioUrl();
      try {
        const speakerId = this.formData.speakerId;
        const extraParams = this.buildExtraParamsForRequest();
        const text = String(extraParams.text ?? '');
        delete extraParams.text;
        const body = {
          model_id: this.formData.model,
          text,
          params: {
            speaker_id: speakerId,
            ...extraParams,
          },
        };
        const blob = await TtsService.synthesize(body);
        this.audioUrl = URL.createObjectURL(blob);
        this.$nextTick(() => {
          if (this.$refs.audioEl) {
            this.$refs.audioEl.play().catch(() => {});
          }
        });
        AnalyticsService.track(EVENTS.TTS_TEST_RUN, { success: true, model_id: this.formData.model });
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Speech generated.',
        });
      } catch (e) {
        AnalyticsService.track(EVENTS.TTS_TEST_RUN, { success: false, model_id: this.formData.model });
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: e.response?.data?.message || e.message || 'Failed to generate speech.',
        });
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.main-content {
  align-items: flex-start;
  gap: 24px;
  padding: 24px;
  overflow: hidden;
}

.left-panel {
  flex: 0 0 420px;
  width: 100%;
  max-width: 420px;
  overflow-y: auto;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.form-content :deep(.tts-select),
.form-content :deep(.tts-file-input),
.form-content :deep(.form-input),
.form-content :deep(.mank-input),
.form-content :deep(.mank-textarea) {
  width: 100%;
}

/* TTS file param: same look as STT audio upload (mank panel fields) */
.form-content :deep(.tts-file-input) {
  background: $light-surface-1;
  border-radius: 8px;
  .v-input__control {
    position: relative;
  }
  .v-input__slot {
    min-height: 32px;
    height: 32px;
    padding: 6px 40px 6px 12px !important;
    align-items: center;
  }
  .v-input__append-inner {
    display: none;
  }
  .v-input__append-outer {
    position: absolute !important;
    right: 12px !important;
    top: 0 !important;
    bottom: 0 !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    pointer-events: none;
  }
  &.v-input--is-focused .v-input__slot fieldset,
  &.v-text-field--outlined fieldset,
  .v-text-field__slot fieldset {
    border: none !important;
  }
  fieldset {
    border: none !important;
  }
  .v-select__selection,
  .v-text-field__slot input,
  .v-file-input__text {
    color: $light-foreground-1;
  }
  .v-file-input__text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.form-group {
  .form-label {
    display: block;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-1;
    margin-bottom: 8px;
    &.required .required-asterisk {
      color: $light-primary;
    }
  }
  .helper-text {
    font-size: $caption-1-size;
    color: $light-foreground-4;
  }
  .token-warning {
    color: $light-warning;
  }
}

.form-actions {
  margin-top: 8px;
}

.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.output-panel-title {
  margin-top: 0;
}

.playback-section {
  background: $light-surface-1;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid $light-border;
}

.empty-playback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: $light-foreground-4;
}

.audio-player-wrap {
  margin-top: 8px;
}

.audio-player {
  width: 100%;
  max-width: 480px;
  height: 48px;
}

.form-content :deep(.v-input) {
  flex: 1 1 auto;
  min-width: 0;
}

.param-bool-checkbox {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>
